/**
 * Klient for innloggingsendepunktene fra #140 (brukt av #141), og én felles
 * oversetting fra serverens feilkoder til bokmål.
 *
 * Alle kall kaster `ApiError` ved ikke-2xx, med `reason` og `retryAfter`
 * fylt inn når serveren sender dem. Nettverksfeil blir `ApiError(0,
 * 'network')` via `asApiError`, så komponentene har én feiltype å vise.
 *
 * Passord holdes bare i komponentens lokale state mens skjemaet er åpent og
 * tømmes etter innsending — de lagres aldri i en store, cache eller
 * sessionStorage.
 */
import type { TFunction } from 'i18next';
import type {
  DeviceStatus,
  LoginBodyInput,
  LoginResponse,
  MemberInviteAcceptInput,
  MemberInvitePreview,
  PasswordChangeInput,
  PasswordResetAcceptInput,
  PasswordResetPreview,
  PinSetInput,
  RosterMember,
} from '@stell/shared';
import { jsonInit, sendJson } from '../../lib/api';
import { formatCountdown } from '../../components/hooks/usePinLockout';

export { asApiError } from '../../lib/api';

const token = (t: string) => encodeURIComponent(t);

export const AuthApi = {
  // Ingen X-Device-Class (#142): på en innrullert kjøkkenskjerm avgjør
  // serveren selv ('tablet', bundet til enheten); ellers blir det 'phone'.
  login: (body: LoginBodyInput) =>
    sendJson<LoginResponse>('/api/auth/login', jsonInit('POST', body)),
  pin: (body: { memberId: string; pin: string }) =>
    sendJson<LoginResponse>('/api/auth/pin', jsonInit('POST', body)),
  /** Er denne nettleseren en innrullert kjøkkenskjerm? (#142) */
  device: () => sendJson<DeviceStatus>('/api/auth/device'),
  /** Avatarrutenettet — bare på innrullert enhet; 404 ellers (#142). */
  roster: () => sendJson<{ members: RosterMember[] }>('/api/auth/roster'),
  logout: () => sendJson<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  changePassword: (body: PasswordChangeInput) =>
    sendJson<{ ok: true }>('/api/auth/password', jsonInit('POST', body)),
  setPin: (body: PinSetInput) => sendJson<{ ok: true }>('/api/auth/pin-code', jsonInit('PUT', body)),
  resetPreview: (t: string) =>
    sendJson<PasswordResetPreview>(`/api/auth/password-reset/${token(t)}`),
  resetAccept: (t: string, body: PasswordResetAcceptInput) =>
    sendJson<{ ok: true }>(`/api/auth/password-reset/${token(t)}/accept`, jsonInit('POST', body)),
  invitePreview: (t: string) => sendJson<MemberInvitePreview>(`/api/members/invite/${token(t)}`),
  inviteAccept: (t: string, body: MemberInviteAcceptInput) =>
    sendJson<{ ok: true }>(`/api/members/invite/${token(t)}/accept`, jsonInit('POST', body)),
};

export interface ApiErrorLike {
  code: string;
  reason?: string | undefined;
  retryAfter?: number | undefined;
}

const WEAK_REASONS = new Set(['too_short', 'too_long', 'same_as_username', 'common', 'same_as_current']);

/**
 * Feilkode fra serveren → vennlig bokmålstekst. Viser aldri råkoden; ukjente
 * koder får `fallback` eller en generell melding.
 */
export function authErrorMessage(t: TFunction, err: ApiErrorLike, fallback?: string): string {
  switch (err.code) {
    case 'invalid_credentials':
      return t('auth.error.invalid_credentials');
    case 'wrong_password':
      return t('auth.error.wrong_password');
    case 'locked':
    case 'rate_limited':
      return err.retryAfter
        ? t('auth.error.locked', { time: formatCountdown(err.retryAfter) })
        : t('auth.error.locked_no_time');
    case 'weak_password':
      return weakPasswordMessage(t, err.reason);
    case 'username_taken':
      return t('auth.error.username_taken');
    case 'invalid_username':
      return t('auth.error.invalid_username');
    case 'forbidden_pin_pattern':
      return t('admin.members.error.forbidden_pin');
    case 'color_slot_taken':
      return t('admin.members.error.color_taken');
    case 'last_admin':
      return t('admin.members.error.last_admin');
    case 'already_enrolled':
      return t('auth.error.already_enrolled');
    case 'unauthenticated':
      return t('auth.error.unauthenticated');
    case 'family_suspended':
      return t('auth.error.family_suspended');
    case 'invalid_or_expired_reset':
      return t('password_reset.invalid_body');
    case 'network':
      return t('auth.error.network');
    default:
      return fallback ?? t('auth.error.unknown');
  }
}

export interface AuthFieldErrors {
  username: string | null;
  /** Nytt passord (`weak_password`). */
  password: string | null;
  /** Nåværende passord (`wrong_password`). */
  current: string | null;
  /** Feil som ikke hører til et felt; null når feilen vises ved et felt. */
  other: string | null;
}

const FIELD_OF: Record<string, keyof Omit<AuthFieldErrors, 'other'>> = {
  username_taken: 'username',
  invalid_username: 'username',
  weak_password: 'password',
  wrong_password: 'current',
};

/**
 * Fordeler en serverfeil på feltet den hører til, så skjemaene viser
 * `username_taken` under brukernavnet, `weak_password` under det nye passordet
 * og `wrong_password` under det nåværende. Resten havner i `other`.
 */
export function splitAuthError(
  t: TFunction,
  err: ApiErrorLike | null,
  fallback?: string,
): AuthFieldErrors {
  const errors: AuthFieldErrors = { username: null, password: null, current: null, other: null };
  if (!err) return errors;
  errors[FIELD_OF[err.code] ?? 'other'] = authErrorMessage(t, err, fallback);
  return errors;
}

/** `too_short` / `common` / … → tekst. Brukes både for serversvar og klientvalidering. */
export function weakPasswordMessage(t: TFunction, reason: string | undefined): string {
  return reason && WEAK_REASONS.has(reason) ? t(`auth.weak.${reason}`) : t('auth.weak.generic');
}
